import { getLogger } from '@logtape/logtape';
import { FilledContentRelationshipField } from '@prismicio/client';
import { useEffect } from 'react';

import useAuth from '@/hooks/useAuth.ts';
import usePrismic from '@/hooks/usePrismic.tsx';

import { EnrichedCustomer, EnrichedDCConfig, EnrichedLocation } from '@/models/EnrichedPrismicDocuments.ts';
import { PrismicDataType } from '@/models/PrismicHookTypes.ts';
import { RoomType } from '@/models/Room.ts';

import { getLocationKeyMap } from '@/services/customerService.ts';

import { useConfigStore } from '@/stores/configStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';
import { usePrismicConfigStore } from '@/stores/prismicConfigStore.ts';

import {
    buildEnrichedConfigArray,
    filterCustomersForUser,
    generateInstanceColors,
    populateInstanceConfigValues,
} from '@/utils/customer-utils.ts';
import { buildPrismicUrl, getTextFromRichTextField } from '@/utils/prismicUtils.ts';

const logger = getLogger(['hook', 'customers']);

const useCustomers = (): void => {
    const { auth_user, customer_ids, instance_ids, location_ids, is_admin } = useAuth();
    const { selected_location, selected_customer, user_customers, available_locations } = useConfigStore();
    const { data: customer_config_data } = usePrismic(PrismicDataType.CUSTOMER_CONFIG);
    const { data: location_data } = usePrismic(PrismicDataType.HUB);
    const { data: dc_instance_config_data } = usePrismic(PrismicDataType.CONFIG_DC_INSTANCE);
    const selected_rooms = useFilterStore((state) => state.selected_rooms);

    const customers = usePrismicConfigStore((state) => state.customers);

    const getAvailableLocations = async (): Promise<void> => {
        const location_key_map = await getLocationKeyMap(customer_ids[0]);
        if (!location_key_map) return;

        const new_available_locations: string[] = [];

        for (const location_id of location_ids) {
            const location_key = location_key_map.get(location_id);
            if (!location_key) continue;

            new_available_locations.push(location_key);
        }

        useConfigStore.setState({ available_locations: new_available_locations });
    };

    useEffect(() => {
        if (!auth_user || available_locations || customer_ids.length === 0 || location_ids.length === 0) return;

        void getAvailableLocations();
    }, [auth_user, customer_ids, location_ids]);

    // TODO: split into utils functions and improve readability
    useEffect(() => {
        if (!customer_config_data || !location_data || !dc_instance_config_data) return;

        const enriched_locations: Record<string, EnrichedLocation> = {};
        for (const location_document_id of Object.keys(location_data)) {
            const location = location_data[location_document_id];
            if (!location) continue;

            const instance_configs = dc_instance_config_data[location_document_id];

            if (!instance_configs || instance_configs.length === 0) {
                logger.info('No instance configs found for location {locationKey}. Skipping', {
                    locationKey: getTextFromRichTextField(location.key),
                    prismicUrl: buildPrismicUrl(location_document_id),
                });
                continue;
            }

            const enriched_configs: EnrichedDCConfig[] = buildEnrichedConfigArray(instance_configs);

            enriched_locations[location_document_id] = {
                ...location,
                id: location_document_id,
                title: getTextFromRichTextField(location.title),
                key: getTextFromRichTextField(location.key),
                configs: enriched_configs,
                // Values are set after filtered for user
                config_map: new Map<string, EnrichedDCConfig>(),
                config_record: {},
                instance_ids: [],
            };
        }

        const enriched_customers: EnrichedCustomer[] = [];

        for (const customer_config of customer_config_data) {
            const customer_key: string = getTextFromRichTextField(customer_config.key);
            if (!customer_key) {
                logger.info('No customer key found for customer {customerKey}. Skipping', {
                    customerKey: getTextFromRichTextField(customer_config.key),
                    prismicUrl: buildPrismicUrl(customer_config.id),
                });
                continue;
            }

            const customer_locations: EnrichedLocation[] = [];
            for (const { location } of customer_config.locations) {
                const document_id: string = (location as FilledContentRelationshipField<'hub'>)?.id;
                if (!document_id) continue;

                const location_obj: EnrichedLocation | undefined = enriched_locations[document_id];
                if (!location_obj) continue;
                customer_locations.push(location_obj);
            }

            if (customer_locations.length === 0) {
                logger.info('No locations found for customer {customerKey}. Skipping', {
                    customerKey: getTextFromRichTextField(customer_config.key),
                    prismicUrl: buildPrismicUrl(customer_config.id),
                });
                continue;
            }

            const enriched_customer_id: string = getTextFromRichTextField(customer_config.customer_id);

            const enriched_customer: EnrichedCustomer = {
                ...customer_config,
                key: getTextFromRichTextField(customer_config.key),
                customer_id: enriched_customer_id,
                customer_name: getTextFromRichTextField(customer_config.customer_name),
                locations: customer_locations,
            };

            enriched_customers.push(enriched_customer);
        }

        const instance_colors: Record<string, string> = generateInstanceColors(Object.values(enriched_locations));

        usePrismicConfigStore.setState({ customers: enriched_customers, instance_colors: instance_colors });
    }, [customer_config_data, location_data, dc_instance_config_data]);

    const setRoomIds = (location: EnrichedLocation): void => {
        if (selected_rooms[location.key]) return;

        const room_ids = (location?.configs ?? []).flatMap(
            (config) =>
                config.rooms?.filter((room) => room.room_type === RoomType.ROOM).map((room) => room.room_id ?? '') ?? []
        );

        if (room_ids.length > 0) {
            useFilterStore.setState((state) => ({
                selected_rooms: {
                    ...state.selected_rooms,
                    [location.key]: room_ids,
                },
            }));
        }
    };

    useEffect(() => {
        if (!auth_user || customers.length === 0 || (!available_locations && !is_admin)) {
            return;
        }

        if (is_admin) {
            const enriched_customers: EnrichedCustomer[] = populateInstanceConfigValues(customers);
            logger.info('Is admin user, loading all customers: {loadedCount}/{totalCount}', {
                loadedCount: enriched_customers.length,
                totalCount: enriched_customers.length,
            });
            useConfigStore.setState({ user_customers: enriched_customers });
            return;
        }

        const customer_id_set = new Set<string>(customer_ids);
        const instance_id_set = new Set<string>(instance_ids);
        const available_locations_set = new Set<string>(available_locations);

        let filtered_customers: EnrichedCustomer[] = filterCustomersForUser(
            customers,
            customer_id_set,
            instance_id_set,
            available_locations_set
        );
        logger.info('Loaded customers for user: {loadedCount}/{totalCount}', {
            loadedCount: filtered_customers.length,
            totalCount: customers.length,
        });

        filtered_customers = populateInstanceConfigValues(filtered_customers);

        useConfigStore.setState({ user_customers: filtered_customers });
    }, [auth_user, customers, is_admin, customer_ids, instance_ids, available_locations]);

    useEffect(() => {
        // For initial load
        if (!auth_user || user_customers.length === 0) return;

        // Get current or default location
        const location: EnrichedLocation | undefined = selected_location ?? user_customers[0]?.locations[0];
        if (!location) return;

        setRoomIds(location);

        // Set default customer and location only on first load
        if (!selected_customer || !selected_location) {
            const default_customer = user_customers[0];
            useConfigStore.setState({
                selected_customer: default_customer,
                selected_location: location,
            });
        } else {
            const selected_customer_obj: EnrichedCustomer | undefined = user_customers.find(
                (customer) => customer.customer_id === selected_customer.customer_id
            );
            const selected_location_obj: EnrichedLocation | undefined = selected_customer_obj?.locations.find(
                (location) => location.key === selected_location.key
            );

            if (!selected_customer_obj || !selected_location_obj) return;

            useConfigStore.setState({
                selected_customer: selected_customer_obj,
                selected_location: selected_location_obj,
            });
        }
    }, [auth_user, user_customers, selected_customer, selected_location, selected_rooms]);
};

export default useCustomers;
